import type { MarketingProject, ProjectIdentity } from '../types/project';

export const CINTA_NATURAL_PROJECT_CODE = 'CN';

export function isCintaNaturalProject(
  project: ProjectIdentity | null | undefined,
): boolean {
  return project?.code === CINTA_NATURAL_PROJECT_CODE;
}

const readFirstString = (
  record: Record<string, unknown>,
  keys: readonly string[],
): string => {
  for (const key of keys) {
    const value = record[key];
    if (typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }

  return '';
};

export function normalizeHexColor(value: unknown): string | null {
  if (typeof value !== 'string') {
    return null;
  }

  const normalized = value.trim().replace(/^#/, '').toUpperCase();
  return /^[0-9A-F]{6}$/.test(normalized) ? `#${normalized}` : null;
}

export function mapProjectIdentity(raw: unknown): ProjectIdentity | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const code = readFirstString(record, ['project_code', 'projectCode', 'code']);
  const name = readFirstString(record, ['project_name', 'projectName', 'name']);
  const colorsRecord = record.colors && typeof record.colors === 'object' && !Array.isArray(record.colors)
    ? record.colors as Record<string, unknown>
    : null;
  const text = normalizeHexColor(
    readFirstString(record, [
      'project_text_color',
      'projectTextColor',
      'text_color',
      'textColor',
    ]),
  ) ?? normalizeHexColor(colorsRecord?.text);
  const background = normalizeHexColor(
    readFirstString(record, [
      'project_bg_color',
      'projectBgColor',
      'bg_color',
      'bgColor',
      'backgroundColor',
    ]),
  ) ?? normalizeHexColor(colorsRecord?.background);

  if (!code || !name || !text || !background) {
    return null;
  }

  return {
    code,
    name,
    colors: {
      text,
      background,
    },
  };
}

export function mapMarketingProject(raw: unknown): MarketingProject | null {
  if (!raw || typeof raw !== 'object' || Array.isArray(raw)) {
    return null;
  }

  const record = raw as Record<string, unknown>;
  const identity = mapProjectIdentity(record);
  const id = typeof record.id === 'number'
    ? record.id
    : Number.parseInt(String(record.id ?? ''), 10);

  if (!identity || !Number.isInteger(id) || id <= 0) {
    return null;
  }

  return {
    id,
    ...identity,
  };
}
